import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Lightbulb, Plus, Trash2, Pencil, Loader2, CheckCircle2, AlertTriangle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_CONFIG = {
  high: { label: 'Alta', color: 'bg-red-500 text-white' },
  medium: { label: 'Média', color: 'bg-amber-500 text-white' },
  low: { label: 'Baixa', color: 'bg-blue-500 text-white' },
};

const STATUS_CONFIG = {
  open: { label: 'Aberta', icon: Clock, color: 'text-amber-500' },
  in_progress: { label: 'Em Andamento', icon: AlertTriangle, color: 'text-blue-500' },
  done: { label: 'Concluída', icon: CheckCircle2, color: 'text-emerald-500' },
};

export function KBRecommendationsPage() {
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', priority: 'medium', category: 'Infraestrutura', status: 'open' });
  const { getAuthHeader } = useAuthStore();

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/kb/recommendations`, { headers: getAuthHeader() });
      setRecs(res.data.recommendations || []);
    } catch (err) { toast.error('Erro ao carregar'); }
    finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    if (!form.title) { toast.error('Título obrigatório'); return; }
    setSaving(true);
    try {
      if (editItem) {
        await axios.put(`${API}/kb/recommendations/${editItem.id}`, form, { headers: getAuthHeader() });
        toast.success('Atualizada');
      } else {
        await axios.post(`${API}/kb/recommendations`, form, { headers: getAuthHeader() });
        toast.success('Recomendação criada');
      }
      setShowForm(false); setEditItem(null);
      setForm({ title: '', description: '', priority: 'medium', category: 'Infraestrutura', status: 'open' });
      fetchData();
    } catch (err) { toast.error('Erro'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir?')) return;
    await axios.delete(`${API}/kb/recommendations/${id}`, { headers: getAuthHeader() });
    toast.success('Excluída'); fetchData();
  };

  const handleStatusChange = async (id, status) => {
    await axios.put(`${API}/kb/recommendations/${id}`, { status }, { headers: getAuthHeader() });
    fetchData();
  };

  if (loading) return <Skeleton className="h-[400px] w-full" />;

  const grouped = { open: [], in_progress: [], done: [] };
  recs.forEach(r => { if (grouped[r.status]) grouped[r.status].push(r); else grouped.open.push(r); });

  return (
    <div className="space-y-5" data-testid="kb-recommendations">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Recomendações</h2>
          <p className="text-sm text-muted-foreground">Melhorias e ações recomendadas para a infraestrutura</p>
        </div>
        <Button onClick={() => { setEditItem(null); setForm({ title: '', description: '', priority: 'medium', category: 'Infraestrutura', status: 'open' }); setShowForm(true); }}>
          <Plus className="h-4 w-4 mr-2" />Nova Recomendação
        </Button>
      </div>

      {/* Kanban-like columns */}
      <div className="grid grid-cols-3 gap-4">
        {Object.entries(STATUS_CONFIG).map(([statusKey, statusMeta]) => {
          const StatusIcon = statusMeta.icon;
          const items = grouped[statusKey] || [];
          return (
            <div key={statusKey}>
              <div className="flex items-center gap-2 mb-3">
                <StatusIcon className={`h-4 w-4 ${statusMeta.color}`} />
                <h3 className="text-sm font-semibold">{statusMeta.label}</h3>
                <Badge variant="secondary" className="text-xs">{items.length}</Badge>
              </div>
              <div className="space-y-2">
                {items.map(r => {
                  const prio = PRIORITY_CONFIG[r.priority] || PRIORITY_CONFIG.medium;
                  return (
                    <Card key={r.id} className="border">
                      <CardContent className="p-3">
                        <div className="flex items-start justify-between mb-1">
                          <Badge className={`text-xs ${prio.color}`}>{prio.label}</Badge>
                          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setEditItem(r); setForm({ title: r.title, description: r.description || '', priority: r.priority, category: r.category || '', status: r.status }); setShowForm(true); }}>
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleDelete(r.id)}>
                              <Trash2 className="h-3 w-3 text-red-500" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-sm font-medium mb-1">{r.title}</p>
                        {r.description && <p className="text-xs text-muted-foreground line-clamp-2">{r.description}</p>}
                        <div className="flex items-center justify-between mt-2">
                          <Badge variant="outline" className="text-xs">{r.category}</Badge>
                          <Select value={r.status} onValueChange={(v) => handleStatusChange(r.id, v)}>
                            <SelectTrigger className="h-6 w-28 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="open">Aberta</SelectItem>
                              <SelectItem value="in_progress">Em Andamento</SelectItem>
                              <SelectItem value="done">Concluída</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
                {items.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">Nenhuma</p>}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={showForm} onOpenChange={(v) => { if (!v) { setShowForm(false); setEditItem(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editItem ? 'Editar Recomendação' : 'Nova Recomendação'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Título</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex: Aumentar monitoramento de disco no grupo DB" /></div>
            <div className="space-y-1"><Label>Descrição</Label><Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Detalhe a recomendação..." /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Prioridade</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="high">Alta</SelectItem><SelectItem value="medium">Média</SelectItem><SelectItem value="low">Baixa</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Categoria</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Infraestrutura" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowForm(false); setEditItem(null); }}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}>{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{editItem ? 'Salvar' : 'Criar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
